import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Mail,
  MessageCircle,
  XCircle,
} from "lucide-react";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { Tabs } from "@/components/ui/Tabs";
import { LogViewer } from "@/components/LogViewer";
import { Toast, type ToastKind } from "@/components/Toast";
import { StatusBadge } from "@/components/StatusBadge";
import { useTournament } from "@/hooks/useTournaments";
import { useClientsStats } from "@/hooks/useClientsStats";
import {
  useEmailSendings,
  useWhatsappSendings,
} from "@/hooks/usePipeline";
import type { SendingRecord, SendingSummary } from "@/lib/api";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/lib/format";
import dayjs from "dayjs";

export default function SendingsMonitor() {
  const { slug } = useParams<{ slug: string }>();
  const [tab, setTab] = useState<"email" | "whatsapp">("email");
  const [filter, setFilter] = useState<"all" | "failed">("all");
  const [toast, setToast] = useState<{ kind: ToastKind; message: string } | null>(null);

  const tournament = useTournament(slug);
  const stats = useClientsStats();
  const emails = useEmailSendings(slug);
  const whatsapps = useWhatsappSendings(slug);

  if (!slug) return <div className="text-sm text-slate-500">Slug mancante.</div>;

  const tName = (tournament.data?.input.tournament?.name as string) || slug;
  const status = (tournament.data?.state?.status as string) || "DRAFT";

  const summary = tab === "email" ? emails.data : whatsapps.data;
  const isLoading = tab === "email" ? emails.isLoading : whatsapps.isLoading;
  const poolSize =
    tab === "email"
      ? stats.data?.email_contactable ?? 0
      : stats.data?.whatsapp_contactable ?? 0;

  const items = summary?.items ?? [];
  const filtered =
    filter === "failed" ? items.filter((r) => !r.success) : items;

  return (
    <div className="flex flex-col gap-5">
      {/* Back */}
      <div>
        <Link
          to={`/tournaments/${slug}`}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-4 w-4" />
          {tName}
        </Link>
      </div>

      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Monitor invii
          </h1>
          <p className="text-sm text-slate-500">
            Email + WhatsApp in real-time con log di sistema.
          </p>
        </div>
        <StatusBadge status={status} />
      </div>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          {
            value: "email",
            label: "Email",
            icon: <Mail className="h-4 w-4" />,
            count: emails.data?.total,
          },
          {
            value: "whatsapp",
            label: "WhatsApp",
            icon: <MessageCircle className="h-4 w-4" />,
            count: whatsapps.data?.total,
          },
        ]}
      />

      {/* KPI row */}
      <SendingsKPIs summary={summary} poolSize={poolSize} loading={isLoading} />

      {/* Two-col */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_380px]">
        {/* Records table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle>Ultimi invii ({filtered.length})</CardTitle>
              <div className="flex gap-1">
                <button
                  onClick={() => setFilter("all")}
                  className={cn(
                    "rounded-md px-2 py-1 text-xs font-medium",
                    filter === "all"
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200",
                  )}
                >
                  Tutti
                </button>
                <button
                  onClick={() => setFilter("failed")}
                  className={cn(
                    "rounded-md px-2 py-1 text-xs font-medium",
                    filter === "failed"
                      ? "bg-red-600 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200",
                  )}
                >
                  Solo errori
                </button>
              </div>
            </div>
          </CardHeader>
          <CardBody className="p-0">
            {filtered.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">
                {isLoading
                  ? "Caricamento…"
                  : filter === "failed"
                    ? "Nessun errore 🎉"
                    : summary?.total === 0
                      ? "Nessun invio effettuato."
                      : "Nessun record"}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Quando</th>
                      <th className="px-3 py-2 text-left font-medium">
                        Destinatario
                      </th>
                      <th className="px-3 py-2 text-left font-medium">Stato</th>
                      <th className="px-3 py-2 text-left font-medium">Errore</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filtered.slice(-100).reverse().map((r, i) => (
                      <SendingRow key={i} record={r} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Log live */}
        <div>
          <LogViewer slug={slug} height="480px" />
          <div className="mt-2 text-xs text-slate-500">
            Stream live dal file{" "}
            <code className="rounded bg-slate-100 px-1 font-mono">
              tournaments_data/logs/{slug}.log
            </code>
          </div>
        </div>
      </div>

      {toast ? (
        <Toast
          kind={toast.kind}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      ) : null}
    </div>
  );
}

function SendingsKPIs({
  summary,
  poolSize,
  loading,
}: {
  summary: SendingSummary | undefined;
  poolSize: number;
  loading: boolean;
}) {
  const sent = summary?.total ?? 0;
  const success = summary?.success ?? 0;
  const failed = summary?.failed ?? 0;
  const pct = poolSize > 0 ? Math.min(100, Math.round((sent / poolSize) * 100)) : 0;

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      <KPI
        icon={<Mail className="h-4 w-4" />}
        label="Inviati"
        value={loading ? "—" : formatNumber(sent)}
        hint={`${pct}% di ${formatNumber(poolSize)}`}
      />
      <KPI
        icon={<CheckCircle2 className="h-4 w-4 text-moving-600" />}
        label="Successi"
        value={loading ? "—" : formatNumber(success)}
      />
      <KPI
        icon={<XCircle className="h-4 w-4 text-red-600" />}
        label="Errori"
        value={loading ? "—" : formatNumber(failed)}
        variant={failed > 0 ? "danger" : undefined}
      />
      <KPI
        icon={<AlertCircle className="h-4 w-4" />}
        label="Pool destinatari"
        value={formatNumber(poolSize)}
        hint="contattabili"
      />
      {/* Progress bar */}
      <div className="col-span-2 md:col-span-4">
        <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
          <span>Avanzamento</span>
          <span className="font-mono">
            {formatNumber(sent)} / {formatNumber(poolSize)}
          </span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-2 rounded-full bg-moving-500 transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function KPI({
  icon,
  label,
  value,
  hint,
  variant,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  variant?: "danger";
}) {
  return (
    <Card>
      <CardBody>
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          {icon}
          {label}
        </div>
        <div
          className={cn(
            "mt-1.5 text-2xl font-semibold",
            variant === "danger" ? "text-red-700" : "text-slate-900",
          )}
        >
          {value}
        </div>
        {hint ? <div className="text-xs text-slate-400">{hint}</div> : null}
      </CardBody>
    </Card>
  );
}

function SendingRow({ record }: { record: SendingRecord }) {
  const at = dayjs(record.sent_at);
  const when = at.isValid() ? at.format("HH:mm:ss") : "—";
  const day = at.isValid() ? at.format("DD/MM") : "";
  return (
    <tr className="hover:bg-slate-50">
      <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-500">
        <span className="font-mono">{when}</span>
        {day ? <span className="ml-1 text-slate-400">{day}</span> : null}
      </td>
      <td className="px-3 py-2 font-mono text-xs text-slate-700">
        {record.target}
      </td>
      <td className="px-3 py-2">
        {record.success ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-moving-700">
            <CheckCircle2 className="h-3.5 w-3.5" />
            OK
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700">
            <XCircle className="h-3.5 w-3.5" />
            errore
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-slate-500">
        {record.error ? (
          <span
            className="line-clamp-1 text-red-700"
            title={record.error}
          >
            {record.error}
          </span>
        ) : (
          "—"
        )}
      </td>
    </tr>
  );
}
